"use client"

import { useCallback, useState, useEffect, useRef } from "react"
import { Plus, FilePenLine, Trash, AlertCircle, X, Filter, Users, Eye, EyeOff } from "lucide-react"
import Modal from "../../components/Modal"
import { getEmpleados, createEmpleado, updateEmpleado, getEmpleadosDev } from "../../services/empleadoService"
import { getSucursales, getSucursalesDev } from "../../services/sucursalService"
import "./Empleado.css"

const ROLES = ["USERS", "ADMIN", "DEVELOPER"]
const MAX_USERS_PER_BRANCH = 7
const FILTERABLE_COLUMNS = ["usuario", "nombre", "cedula", "sucursal", "email", "mac"]

const Empleado = () => {
  const [sucursales, setSucursales] = useState([])
  const [empleados, setEmpleados] = useState([])
  const [formData, setFormData] = useState({
    id: "",
    usuario: "",
    contraseña: "",
    nombre: "",
    cedula: "",
    celular: "",
    email: "",
    rol: "USERS",
    estado: true,
    mac: null,
    sucursal: { id: "" },
  })
  const [editing, setEditing] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [empleadoToDelete, setEmpleadoToDelete] = useState(null)
  const [filter, setFilter] = useState("active")
  const [errors, setErrors] = useState({
    usuario: "",
    cedula: "",
    email: "",
    sucursal: "",
  })
  const [role, setRole] = useState(localStorage.getItem("role"))
  const sucursalId = localStorage.getItem("idsucursal")
  const [usersPerBranch, setUsersPerBranch] = useState({})
  const [showLimitAlert, setShowLimitAlert] = useState(false)
  const [currentSucursalNombre, setCurrentSucursalNombre] = useState("")
  const [filters, setFilters] = useState({
    usuario: "",
    nombre: "",
    cedula: "",
    email: "",
    sucursal: "",
    mac: "",
  })
  const [activeFilterColumn, setActiveFilterColumn] = useState(null)
  const filterInputRef = useRef(null)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    const handleStorageChange = () => {
      setRole(localStorage.getItem("role"))
    }

    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [])

  useEffect(() => {
    if (activeFilterColumn && filterInputRef.current) {
      filterInputRef.current.focus()
    }
  }, [activeFilterColumn])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterInputRef.current &&
        !filterInputRef.current.contains(event.target) &&
        !event.target.closest(".column-header")
      ) {
        setActiveFilterColumn(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const fetchAllEmpleados = useCallback(async () => {
    try {
      if (role === "DEVELOPER") {
        const data = await getEmpleadosDev()
        setEmpleados(data)
      } else if (sucursalId) {
        const data = await getEmpleados(sucursalId)
        setEmpleados(data)
        const countByBranch = {}
        data.forEach((emp) => {
          if (emp.estado === true && emp.sucursal && emp.sucursal.id) {
            const branchId = emp.sucursal.id
            countByBranch[branchId] = (countByBranch[branchId] || 0) + 1
          }
        })
        setUsersPerBranch(countByBranch)
      }
    } catch (error) {
      console.error(error.message)
    }
  }, [role, sucursalId])

  const fetchSucursales = useCallback(async () => {
    try {
      const data = role === "DEVELOPER" ? await getSucursalesDev() : await getSucursales()
      setSucursales(data)
      if (sucursalId) {
        const currentSucursal = data.find((s) => s.id === sucursalId)
        if (currentSucursal) {
          setCurrentSucursalNombre(currentSucursal.nombre)
        }
      }
    } catch (error) {
      console.error("Error al cargar las sucursales:", error.message)
    }
  }, [role, sucursalId])

  useEffect(() => {
    fetchAllEmpleados()
    fetchSucursales()
  }, [fetchAllEmpleados, fetchSucursales])

  useEffect(() => {
    const handleSucursalChange = (event) => {
      fetchAllEmpleados()
      fetchSucursales()
    }
    window.addEventListener("sucursalChange", handleSucursalChange)
    return () => {
      window.removeEventListener("sucursalChange", handleSucursalChange)
    }
  }, [fetchAllEmpleados, fetchSucursales])

  useEffect(() => {
    if (isModalOpen) {
      fetchSucursales()
    }
  }, [isModalOpen, fetchSucursales])

  useEffect(() => {
    let timer
    if (showLimitAlert) {
      timer = setTimeout(() => {
        setShowLimitAlert(false)
      }, 8000)
    }
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [showLimitAlert])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    if (name === "estado" && type === "checkbox") {
      setFormData({ ...formData, estado: checked })
      return
    }

    if (name === "sucursal.id") {
      if (!editing && value) {
        const currentCount = usersPerBranch[value] || 0
        if (currentCount >= MAX_USERS_PER_BRANCH) {
          setErrors((prev) => ({
            ...prev,
            sucursal: `Esta sucursal ya tiene ${MAX_USERS_PER_BRANCH} usuarios activos. No se pueden crear más.`,
          }))
        } else {
          setErrors((prev) => ({
            ...prev,
            sucursal: "",
          }))
        }
      }

      setFormData((prev) => ({
        ...prev,
        sucursal: {
          ...prev.sucursal,
          id: value,
        },
      }))
      return
    }

    setFormData({ ...formData, [name]: value })
  }

  const validateForm = () => {
    const newErrors = { usuario: "", cedula: "", email: "", sucursal: "" }
    let isValid = true

    const usuarioExists = empleados.some((emp) => emp.usuario === formData.usuario && emp.id !== formData.id)
    if (usuarioExists) {
      newErrors.usuario = "El usuario ya está en uso."
      isValid = false
    }

    const cedulaExists = empleados.some((emp) => emp.cedula === formData.cedula && emp.id !== formData.id)
    if (cedulaExists) {
      newErrors.cedula = "La cédula ya está registrada."
      isValid = false
    }

    const emailExists = empleados.some((emp) => emp.email === formData.email && emp.id !== formData.id)
    if (emailExists) {
      newErrors.email = "El correo ya está registrado."
      isValid = false
    }

    if (!editing && formData.sucursal.id) {
      const currentCount = usersPerBranch[formData.sucursal.id] || 0
      if (currentCount >= MAX_USERS_PER_BRANCH) {
        newErrors.sucursal = `Esta sucursal ya tiene ${MAX_USERS_PER_BRANCH} usuarios activos. No se pueden crear más.`
        isValid = false
      }
    }

    setErrors(newErrors)
    return isValid
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    if (editing) {
      try {
        await updateEmpleado(formData.id, formData)
        fetchAllEmpleados()
        closeModal()
      } catch (error) {
        console.error(error.message)
      }
    } else {
      try {
        const newEmpleado = {
          ...formData,
          estado: true,
        }
        await createEmpleado(newEmpleado)
        fetchAllEmpleados()
        closeModal()
      } catch (error) {
        console.error(error.message)
      }
    }
  }

  const handleNew = () => {
    const currentBranchCount = usersPerBranch[sucursalId] || 0
    const currentBranchFull = currentBranchCount >= MAX_USERS_PER_BRANCH

    if (currentBranchFull && role !== "DEVELOPER") {
      setShowLimitAlert(true)
      return
    }

    setFormData({
      id: "",
      usuario: "",
      contraseña: "",
      nombre: "",
      cedula: "",
      celular: "",
      email: "",
      rol: "USERS",
      estado: true,
      mac: null,
      sucursal: { id: "" },
    })
    setEditing(false)
    setIsModalOpen(true)
    setErrors({
      usuario: "",
      cedula: "",
      email: "",
      sucursal: "",
    })
  }

  const handleEdit = (empleado) => {
    setFormData({
      id: empleado.id,
      usuario: empleado.usuario,
      contraseña: "",
      nombre: empleado.nombre,
      cedula: empleado.cedula,
      celular: empleado.celular || "",
      email: empleado.email,
      rol: empleado.rol || "USERS",
      estado: empleado.estado !== false,
      mac: empleado.mac || null,
      sucursal: { id: empleado.sucursal?.id || "" },
    })
    setEditing(true)
    setIsModalOpen(true)
    setErrors({
      usuario: "",
      cedula: "",
      email: "",
      sucursal: "",
    })
  }

  const openDeleteConfirmation = (empleado) => {
    setEmpleadoToDelete(empleado)
  }

  const confirmDelete = async () => {
    if (empleadoToDelete) {
      const updated = { ...empleadoToDelete, estado: false }
      try {
        await updateEmpleado(empleadoToDelete.id, updated)
        fetchAllEmpleados()
      } catch (error) {
        console.error(error.message)
      }
      closeDeleteModal()
    }
  }

  const closeDeleteModal = () => {
    setEmpleadoToDelete(null)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const handleFilterChange = (e) => {
    setFilter(e.target.value)
  }

  const handleColumnFilterChange = (e) => {
    setFilters({
      ...filters,
      [activeFilterColumn]: e.target.value,
    })
  }

  const toggleColumnFilter = (column) => {
    if (!FILTERABLE_COLUMNS.includes(column) || role !== "DEVELOPER") {
      return
    }

    if (activeFilterColumn === column) {
      setActiveFilterColumn(null)
    } else {
      setActiveFilterColumn(column)
    }
  }

  const clearFilter = (column) => {
    setFilters({
      ...filters,
      [column]: "",
    })
    setActiveFilterColumn(null)
  }

  const filteredEmpleados = empleados.filter((emp) => {
    if (filter === "active" && emp.estado !== true) {
      return false
    }
    if (filter === "inactive" && emp.estado !== false) {
      return false
    }
    if (filters.usuario && !emp.usuario?.toLowerCase().includes(filters.usuario.toLowerCase())) {
      return false
    }
    if (filters.nombre && !emp.nombre?.toLowerCase().includes(filters.nombre.toLowerCase())) {
      return false
    }
    if (filters.cedula && !emp.cedula?.toLowerCase().includes(filters.cedula.toLowerCase())) {
      return false
    }
    if (filters.email && !emp.email?.toLowerCase().includes(filters.email.toLowerCase())) {
      return false
    }
    if (filters.sucursal) {
      const sucursalNombre = emp.sucursal?.nombre || ""
      if (!sucursalNombre.toLowerCase().includes(filters.sucursal.toLowerCase())) {
        return false
      }
    }
    if (filters.mac) {
      const mac = emp.mac || "Sin Mac asignada"
      if (!mac.toLowerCase().includes(filters.mac.toLowerCase())) {
        return false
      }
    }
    return true
  })

  const getEmployeeCounts = () => {
    const activeCount = empleados.filter((emp) => emp.estado === true).length
    const inactiveCount = empleados.filter((emp) => emp.estado === false).length
    return { activeCount, inactiveCount }
  }

  const renderColumnHeader = (title, column) => {
    const isFilterable = FILTERABLE_COLUMNS.includes(column) && role === "DEVELOPER"
    const isActive = activeFilterColumn === column
    const hasFilter = filters[column] && filters[column].length > 0

    return (
      <th
        className={`column-header ${isFilterable ? "filterable" : ""} ${isActive ? "active-filter" : ""} ${hasFilter ? "has-filter" : ""}`}
        onClick={() => isFilterable && toggleColumnFilter(column)}
      >
        <div className="column-header-content">
          <span>{title}</span>
          {isFilterable && <Filter size={14} className={`filter-icon ${hasFilter ? "filter-active" : ""}`} />}
        </div>
        {isActive && (
          <div className="column-filter-container" onClick={(e) => e.stopPropagation()}>
            <input
              ref={filterInputRef}
              type="text"
              className="column-filter"
              placeholder={`Filtrar por ${title.toLowerCase()}...`}
              value={filters[column] || ""}
              onChange={handleColumnFilterChange}
            />
            {filters[column] && (
              <button className="clear-filter-btn" onClick={() => clearFilter(column)} aria-label="Limpiar filtro">
                <X size={14} />
              </button>
            )}
          </div>
        )}
      </th>
    )
  }

  const renderEmployeeCounter = () => {
    if (role !== "DEVELOPER") return null
    const { activeCount, inactiveCount } = getEmployeeCounts()
    return (
      <div className="employee-counter">
        <Users size={16} className="counter-icon" />
        {filter === "active" && (
          <div className="counter-item">
            <span className="counter-label">Usuarios activos:</span>
            <span className="counter-value counter-active">{activeCount}</span>
          </div>
        )}
        {filter === "inactive" && (
          <div className="counter-item">
            <span className="counter-label">Usuarios inactivos:</span>
            <span className="counter-value counter-inactive">{inactiveCount}</span>
          </div>
        )}
        {filter === "all" && (
          <>
            <div className="counter-item">
              <span className="counter-label">Activos:</span>
              <span className="counter-value counter-active">{activeCount}</span>
            </div>
            <div className="counter-item">
              <span className="counter-label">Inactivos:</span>
              <span className="counter-value counter-inactive">{inactiveCount}</span>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="empleado-container">
      <div className="empleado-header">
        <h1 className="title-empleado">Gestión de Usuarios</h1>
        <div className="header-decoration"></div>
      </div>
      {showLimitAlert && (
        <div
          className="limit-alert"
          style={{
            backgroundColor: "rgba(245, 124, 0, 0.1)",
            border: "1px solid rgba(245, 124, 0, 0.3)",
            borderRadius: "var(--radius-md)",
            padding: "16px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            position: "relative",
          }}
        >
          <AlertCircle size={20} style={{ color: "var(--warning)", flexShrink: 0, marginTop: "2px" }} />
          <div>
            <h3
              style={{
                color: "var(--warning)",
                fontSize: "var(--font-size-md)",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              Límite de usuarios alcanzado
            </h3>
            <p style={{ color: "var(--text-primary)", fontSize: "var(--font-size-sm)" }}>
              La sucursal <strong>{currentSucursalNombre}</strong> ha alcanzado el límite de {MAX_USERS_PER_BRANCH}{" "}
              usuarios activos. Si necesita crear más usuarios, por favor comuníquese con soporte técnico.
            </p>
          </div>
          <button
            onClick={() => setShowLimitAlert(false)}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "var(--text-muted)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
            }}
            aria-label="Cerrar alerta"
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.05)")}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <X size={16} />
          </button>
        </div>
      )}
      <div className="table-container">
        <div className="table-header">
          <div className="filter-actions">
            <div className="filter-container">
              <label>Estado:</label>
              <select onChange={handleFilterChange} value={filter}>
                <option value="active">Activos</option>
                <option value="inactive">Inactivos</option>
                <option value="all">Todos</option>
              </select>
            </div>
            {renderEmployeeCounter()}
          </div>
          <div>
            <button onClick={handleNew} className="btn-primary">
              <Plus size={16} />
              Nuevo Usuario
            </button>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table-empleados">
            <thead>
              <tr>
                <th>ID</th>
                {renderColumnHeader("Usuario", "usuario")}
                {renderColumnHeader("Nombre", "nombre")}
                {renderColumnHeader("Cédula", "cedula")}
                <th>Celular</th>
                {renderColumnHeader("Email", "email")}
                {renderColumnHeader("Sucursal", "sucursal")}
                {renderColumnHeader("MAC", "mac")}
                <th>Rol</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmpleados.length > 0 ? (
                filteredEmpleados.map((emp) => (
                  <tr key={emp.id}>
                    <td>
                      <span className="empleado-id">{emp.id}</span>
                    </td>
                    <td>{emp.usuario}</td>
                    <td>{emp.nombre}</td>
                    <td>{emp.cedula}</td>
                    <td>{emp.celular || ""}</td>
                    <td>{emp.email}</td>
                    <td>{emp.sucursal?.nombre || "Sin Sucursal"}</td>
                    <td>{emp.mac || "Sin Mac asignada"}</td>
                    <td>
                      <span className={`rol-badge rol-${emp.rol?.toLowerCase() || "users"}`}>{emp.rol || "USERS"}</span>
                    </td>
                    <td>
                      <span className={`estado-badge ${emp.estado ? "estado-activo" : "estado-inactivo"}`}>
                        {emp.estado ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => handleEdit(emp)} className="action-button">
                          <FilePenLine size={18} className="edit-icon" />
                        </button>
                        {emp.estado === true && (
                          <button onClick={() => openDeleteConfirmation(emp)} className="action-button">
                            <Trash size={18} className="delete-icon" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11">
                    <div className="empty-message">No hay usuarios registrados</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modal para Crear/Actualizar Empleado */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div className="usu-modal-container">
          <div className="usu-modal-header">
            <h2 className="usu-modal-title">{editing ? "Actualizar Usuario" : "Crear Usuario"}</h2>
          </div>
          <div className="usu-modal-content">
            <form onSubmit={handleSubmit}>
              <div className="usu-modal-form-grid">
                {/* Usuario */}
                <div className="modal-form-group">
                  <label className="modal-label">Usuario:</label>
                  <input
                    type="text"
                    name="usuario"
                    value={formData.usuario}
                    onChange={handleChange}
                    required
                    className="modal-input"
                  />
                  {errors.usuario && <p className="error-message">{errors.usuario}</p>}
                </div>

                {/* Contraseña */}
                <div className="modal-form-group">
                  <label className="modal-label">Contraseña:</label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="contraseña"
                      value={formData.contraseña}
                      onChange={handleChange}
                      required={!editing}
                      className="modal-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="action-button"
                      style={{
                        position: "absolute",
                        right: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--text-secondary)",
                      }}
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Nombre */}
                <div className="modal-form-group">
                  <label className="modal-label">Nombre:</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="modal-input"
                  />
                </div>

                {/* Cédula */}
                <div className="modal-form-group">
                  <label className="modal-label">Cédula:</label>
                  <input
                    type="text"
                    name="cedula"
                    value={formData.cedula}
                    onChange={handleChange}
                    required
                    className="modal-input"
                  />
                  {errors.cedula && <p className="error-message">{errors.cedula}</p>}
                </div>

                {/* Celular */}
                <div className="modal-form-group">
                  <label className="modal-label">Celular:</label>
                  <input
                    type="text"
                    name="celular"
                    value={formData.celular}
                    onChange={handleChange}
                    className="modal-input"
                  />
                </div>

                {/* Email */}
                <div className="modal-form-group">
                  <label className="modal-label">Email:</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="modal-input"
                  />
                  {errors.email && <p className="error-message">{errors.email}</p>}
                </div>

                {/* MAC */}
                <div className="modal-form-group">
                  <label className="modal-label">MAC:</label>
                  {role === "ADMIN" ? (
                    <>
                      <div
                        className="modal-input"
                        style={{
                          backgroundColor: "var(--background)",
                          color: "var(--text-muted)",
                          cursor: "not-allowed",
                          userSelect: "none",
                        }}
                      >
                        {formData.mac || "Sin MAC asignada"}
                      </div>
                      <p className="error-message">Solo usuarios autorizados pueden editar la MAC</p>
                    </>
                  ) : (
                    <input
                      type="text"
                      name="mac"
                      value={formData.mac || ""}
                      onChange={handleChange}
                      className="modal-input"
                    />
                  )}
                </div>

                {/* Rol */}
                <div className="modal-form-group">
                  <label className="modal-label">Rol:</label>
                  <select
                    name="rol"
                    value={formData.rol}
                    onChange={handleChange}
                    required
                    className="modal-select"
                    disabled={role === "ADMIN"}
                  >
                    {ROLES.map((rol) => (
                      <option key={rol} value={rol}>
                        {rol}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Estado */}
                {editing && (
                  <div className="modal-form-group">
                    <label className="modal-label">Estado:</label>
                    <select
                      name="estado"
                      value={formData.estado ? "activo" : "inactivo"}
                      onChange={(e) => {
                        const estado = e.target.value === "activo"
                        setFormData({ ...formData, estado })
                      }}
                      className="modal-select"
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                )}

                {/* Sucursal */}
                <div className="modal-form-group usu-modal-form-full">
                  <label className="modal-label">Sucursal:</label>
                  <select
                    name="sucursal.id"
                    value={formData.sucursal.id}
                    onChange={handleChange}
                    required
                    className="modal-select"
                    disabled={role === "ADMIN" && editing}
                  >
                    <option value="">-- Seleccione una sucursal --</option>
                    {sucursales.map((sucursal) => {
                      const userCount = usersPerBranch[sucursal.id] || 0
                      const isFull = userCount >= MAX_USERS_PER_BRANCH && !editing
                      return (
                        <option key={sucursal.id} value={sucursal.id} disabled={isFull && role !== "DEVELOPER"}>
                          {sucursal.nombre}{" "}
                          {isFull && role !== "DEVELOPER"
                            ? `(Límite alcanzado: ${userCount}/${MAX_USERS_PER_BRANCH})`
                            : ""}
                        </option>
                      )
                    })}
                  </select>
                  {errors.sucursal && <p className="error-message">{errors.sucursal}</p>}
                </div>
              </div>
              <div className="usu-modal-actions">
                <button type="button" onClick={closeModal} className="modal-btn modal-btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="modal-btn modal-btn-primary">
                  {editing ? "Actualizar" : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Modal>

      {/* Modal de confirmación para "eliminar" (estado=false) */}
      <Modal isOpen={empleadoToDelete !== null} onClose={closeDeleteModal}>
        <div className="modal-header">
          <h2 className="modal-title">Confirmación</h2>
        </div>
        <div className="modal-content">
          <p className="modal-form-full">
            ¿Está seguro que desea eliminar al usuario <strong>{empleadoToDelete && empleadoToDelete.nombre}</strong>?
          </p>
          <div className="modal-actions">
            <button onClick={closeDeleteModal} className="modal-btn modal-btn-secondary">
              Cancelar
            </button>
            <button onClick={confirmDelete} className="modal-btn modal-btn-primary">
              Confirmar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Empleado